import { mapCategoryToEdgeCategory } from '@modules/common/utils';
import { useTaxonomyStore } from '@modules/taxonomy/store/taxonomyStore';
import { GeneratedRcInput, Rc, UnassignedRc } from 'generated/graphql';
import { useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { useGetRCsForFeedbackRecord } from './useGetRCsForFeedbackRecord';
import { useGetRCsFromReasonDescription } from './useGetRCsFromReasonDescription';

interface UseGetReasonDefinitionsOptions {
  abortSignal?: AbortSignal;
}

export const useGetReasonDefinitions = ({
  abortSignal,
}: UseGetReasonDefinitionsOptions) => {
  const { selectedFeedbackRecordID, selectedReasonDefinition } =
    useTaxonomyStore(
      ({ createReasonV3State: { misc } }) => ({
        selectedFeedbackRecordID: misc?.selectedFeedbackRecordID,
        selectedReasonDefinition: misc?.selectedReasonDefinition,
      }),
      shallow,
    );

  const [getRCsFromDescription, { isLoading: isFetchingRCsFromDescription }] =
    useGetRCsFromReasonDescription({
      abortSignal,
    });

  const [getRCsForRecord, { isLoading: isFetchingRCsForRecord }] =
    useGetRCsForFeedbackRecord({
      abortSignal,
    });

  const getReasonDefinitions = useCallback(
    async (description: string) => {
      const { data, error } = await getRCsFromDescription(description);

      const reasonDefinitionsFromResponse =
        data?.getRCsFromDescription?.rcs ?? [];

      const selectedUnassignedRC: UnassignedRc = {
        __typename: 'UnassignedRC',
        name: selectedReasonDefinition?.name ?? '',
        category: mapCategoryToEdgeCategory(selectedReasonDefinition?.category),
        isSelected: true,
        recordIDs: [],
      };

      const reasonDefinitions = [...reasonDefinitionsFromResponse];

      if (selectedUnassignedRC.name && selectedUnassignedRC.category) {
        reasonDefinitions.push(selectedUnassignedRC);
      }

      const uniqueReasonDefinitionByName: Record<string, Rc> = {};

      if (selectedFeedbackRecordID) {
        const generatedRCInput = reasonDefinitions
          .filter((rc) => rc.__typename === 'GeneratedRC')
          .map((rc): GeneratedRcInput => {
            return {
              name: rc.name,
              category: rc.category,
            };
          });

        const { data } = await getRCsForRecord({
          generatedRCs: generatedRCInput ?? [],
          recordID: selectedFeedbackRecordID,
        });

        const reasonDefinitionsForRecord = data?.getRCsForRecord?.rcs;

        if (reasonDefinitionsForRecord && reasonDefinitionsForRecord.length) {
          reasonDefinitions.push(...reasonDefinitionsForRecord);
        }
      }

      // Filter duplicate ones
      reasonDefinitions.forEach((definition) => {
        if (definition.name) {
          uniqueReasonDefinitionByName[definition.name] = definition;
        }
      });

      const filteredReasonDefinitions = Object.values(
        uniqueReasonDefinitionByName,
      );

      return { definitions: filteredReasonDefinitions, error };
    },
    [
      getRCsForRecord,
      getRCsFromDescription,
      selectedFeedbackRecordID,
      selectedReasonDefinition,
    ],
  );

  return [
    getReasonDefinitions,
    { loading: isFetchingRCsFromDescription || isFetchingRCsForRecord },
  ] as const;
};
